import { ErrorMessage, Field, FieldProps } from '@atlaskit/form';
import Select, { OptionProps, ValueType } from '@atlaskit/select';
import {
  CommerceAddress,
  CreditCard,
  InvoiceGroup,
  InvoiceGroupPurchaseOrder,
  Maybe,
  PaymentMethod,
  PaymentMethodType,
  PaymentOnTerms,
  Scalars,
} from '@css-xp/graphtypes';
import { EntitlementTermDetails } from '@css-xp/graphtypes/src/generated-types-new';
import { ENABLE_CREDIT_MANAGEMENT } from '@atlassian/cssxp-utils-generic';
import { isJiraProduct } from '@atlassian/cssxp-utils-product';
import { defaultTo, get, startCase } from 'lodash';
import findIndex from 'lodash/findIndex';
import React from 'react';
import { PricingType } from '@atlassian/cssxp-types-promotion';
import { PaymentMethodSelectDetailsLine } from '../../common-tabs';
import { useFeatureToggle } from '../../hooks';
import BillingAddressDetailsLine from './billing-address-details-line';
import './invoice-group-form-field.scss';

export interface InvoiceGroupSelectItem {
  value: string; // invoiceGroupId
  label?: string;
  invoiceGroup: InvoiceGroup;
}

export enum InvoiceGroupFormFieldErrors {
  SHIPPING_ADDRESS = 'SHIPPING_ADDRESS',
  PAYMENT_METHOD = 'PAYMENT_METHOD',
  PAYMENT_METHOD_NOT_ELIGIBLE = 'PAYMENT_METHOD_NOT_ELIGIBLE',
}

const getPurchaseOrderDetails = (purchaseDetails: InvoiceGroupPurchaseOrder) => {
  if (purchaseDetails?.oneTimeUse) {
    return purchaseDetails.number + ' ' + '(Single-use)';
  }
  return purchaseDetails.number + ' ' + '(Multi-use)';
};

const formatAddressLabel = (node: CommerceAddress) => {
  let formatAddressLabel = '';
  if (node?.name) {
    formatAddressLabel += node.name;
  }
  if (node?.addressLine1) {
    formatAddressLabel += `, ${node.addressLine1}`;
  }
  if (node?.addressLine2) {
    formatAddressLabel += `, ${node.addressLine2}`;
  }
  if (node?.postcode) {
    formatAddressLabel += `, ${node.postcode}`;
  }
  if (node?.city) {
    formatAddressLabel += `, ${node.city}`;
  }
  if (node?.state) {
    formatAddressLabel += `, ${node.state}`;
  }
  if (node?.country) {
    formatAddressLabel += `, ${node.country}`;
  }
  return formatAddressLabel;
};

const formatCreditCardLabel = (paymentMethod: PaymentMethod, creditDetails?: PushPaymentCreditDetails) => {
  switch (paymentMethod?.type) {
    case PaymentMethodType.CARD: {
      const creditCard = paymentMethod as CreditCard;
      return `Credit card: **** ${creditCard?.card?.last4Numbers} (${creditCard?.card?.brand?.toUpperCase()}), exp - ${
        creditCard?.card?.expirationMonth
      }/${creditCard?.card?.expirationYear}`;
    }
    case PaymentMethodType.DEFERRED: {
      const paymentOnTerms = paymentMethod as PaymentOnTerms;
      const pushPaymentDueDays = paymentOnTerms.terms?.invoiceDueDays;
      return `Net-${pushPaymentDueDays}, Credit limit available: ${defaultTo(
        get(creditDetails, 'availableCreditLimit'),
        0
      )} Total credit limit: ${defaultTo(get(creditDetails, 'totalCreditLimit'), 0)}.`;
    }
    default:
      return `Unknown payment method`;
  }
};

const InvoiceGroupSelectOption = ({
  data,
  getStyles,
  innerProps,
  innerRef,

  ...props
}: OptionProps<InvoiceGroupSelectItem>) => {
  const { invoiceGroup, creditDetails } = data as { invoiceGroup: any; creditDetails: PushPaymentCreditDetails };
  const isNameNone = invoiceGroup.name && invoiceGroup.name?.toLowerCase() !== 'none';
  const optionStyles = getStyles('option', props);

  return (
    <section ref={innerRef} {...innerProps} style={optionStyles} key={`select-option-${invoiceGroup.id}`}>
      <span data-testid={`select-option-${invoiceGroup.id}`}>
        <strong>
          {isNameNone ? invoiceGroup.name : 'Unnamed invoice group'} ({invoiceGroup.currency})
        </strong>
        <BillingAddressDetailsLine address={invoiceGroup?.shipToParty} noAddressLabel="Same as billing address" />
        <PaymentMethodSelectDetailsLine
          paymentMethod={invoiceGroup.defaultPaymentMethod}
          creditDetails={creditDetails}
        />
        {invoiceGroup.purchaseOrder && invoiceGroup.purchaseOrder?.number ? (
          <div>
            Purchase order:
            {` ${getPurchaseOrderDetails(invoiceGroup.purchaseOrder)}`}
          </div>
        ) : (
          <div>
            <em>No purchase order</em>
          </div>
        )}
        {invoiceGroup?.shipToPriceEligibility ? (
          <div>Pricing eligibility: {startCase(invoiceGroup?.shipToPriceEligibility ?? '')}</div>
        ) : (
          <div>
            <em>No Pricing eligibility</em>
          </div>
        )}
      </span>
    </section>
  );
};

const formatLabelForSearchWithinDropDown = (
  invoiceGroup: InvoiceGroup,
  creditDetails?: PushPaymentCreditDetails
): string => {
  let formattedLabel = '';
  if (invoiceGroup?.name && invoiceGroup?.name?.toLowerCase() !== 'none') {
    formattedLabel += invoiceGroup.name;
  } else {
    formattedLabel += `Unnamed invoice group`;
  }

  if (invoiceGroup?.currency) {
    formattedLabel += ` (${invoiceGroup.currency})`;
  }

  if (invoiceGroup?.shipToParty) {
    formattedLabel += `, ${formatAddressLabel(invoiceGroup.shipToParty)}`;
  } else {
    formattedLabel += `, Same as billing address`;
  }

  if (invoiceGroup?.defaultPaymentMethod) {
    formattedLabel += `, ${formatCreditCardLabel(invoiceGroup.defaultPaymentMethod, creditDetails)}`;
  } else {
    formattedLabel += `, No payment method`;
  }

  if (invoiceGroup?.purchaseOrder && invoiceGroup?.purchaseOrder?.number) {
    formattedLabel += `, Purchase order: ${getPurchaseOrderDetails(invoiceGroup.purchaseOrder)}`;
  } else {
    formattedLabel += `, No purchase order`;
  }

  if (invoiceGroup?.shipToPriceEligibility) {
    formattedLabel += `, Pricing eligibility: `;
    formattedLabel += startCase(invoiceGroup.shipToPriceEligibility);
  }

  return formattedLabel;
};

interface PushPaymentCreditDetails {
  totalCreditLimit: number;
  availableCreditLimit: number;
}
interface Props {
  fieldName?: string;
  fieldLabel?: React.ReactNode;
  availableInvoiceGroups?: InvoiceGroup[] | null;
  entitlementBillingInvoiceGroupId?: string | null;
  scheduledBillingInvoiceGroupId?: string | null;
  isRequired?: boolean;
  isDisabled?: boolean | false;
  productId?: string | undefined;
  // specifically shows the invoice groups which are related to term payment method
  // By default it's value is true so that it can impact only when passed as false from parent
  showTermPaymentMethodGroup?: boolean | undefined | null;
  maxWidth?: null;
  testId?: string;
  onChange?: FieldProps<ValueType<InvoiceGroupSelectItem>, HTMLSelectElement>['onChange'];
  validate?: (
    value: ValueType<InvoiceGroupSelectItem> | undefined,
    formState: Object,
    fieldState: Object
  ) => string | void | Promise<string | void>;
  setSelectedInvoiceGroupPricingPlanValue?: (shipToPriceEligibility?: Maybe<Scalars['String']>) => void;
  rewindPaneDefault?: boolean | false;
  isReadOnly?: boolean;
  purchaseOrder?: InvoiceGroupPurchaseOrder;
  isLoading?: boolean;
  creditDetails?: EntitlementTermDetails | null | undefined;
  currentPlan?: PricingType;
  isSearchable?: boolean;
}

export const InvoiceGroupFormField = ({
  fieldName = 'invoiceGroupId',
  fieldLabel = 'Invoice group',
  isDisabled,
  isRequired = false,
  availableInvoiceGroups = [],
  entitlementBillingInvoiceGroupId = null,
  scheduledBillingInvoiceGroupId = null,
  productId,
  showTermPaymentMethodGroup = true,
  maxWidth,
  testId,
  onChange,
  validate,
  setSelectedInvoiceGroupPricingPlanValue,
  rewindPaneDefault,
  isReadOnly = false,
  purchaseOrder,
  isLoading = false,
  creditDetails,
  currentPlan,
  isSearchable,
}: Props) => {
  const [isEnabled] = useFeatureToggle();
  const isCreditManagementEnabled = isEnabled(ENABLE_CREDIT_MANAGEMENT);
  const shouldHidePaymentOnTerms =
    (productId && !isCreditManagementEnabled && isJiraProduct(productId)) ||
    !showTermPaymentMethodGroup ||
    [PricingType.FREE, PricingType.LIMITED_FREE].includes(currentPlan as PricingType);

  let _availableInvoiceGroups: InvoiceGroup[] = availableInvoiceGroups ?? [];

  if (shouldHidePaymentOnTerms) {
    _availableInvoiceGroups = _availableInvoiceGroups.filter(
      (invoiceGroup: InvoiceGroup): boolean => invoiceGroup?.defaultPaymentMethod?.type !== PaymentMethodType.DEFERRED
    );
  }
  const invoiceGroupSelectItems = _availableInvoiceGroups.map(
    (invoiceGroup: InvoiceGroup): InvoiceGroupSelectItem => {
      return {
        invoiceGroup,
        creditDetails,
        label: formatLabelForSearchWithinDropDown(invoiceGroup),
        value: invoiceGroup.id,
      } as InvoiceGroupSelectItem;
    }
  );

  const getDefaultBillingGroup = (invoiceGroupId: string): InvoiceGroupSelectItem | undefined => {
    return invoiceGroupSelectItems.find((invoiceGroupItem: InvoiceGroupSelectItem): boolean => {
      return invoiceGroupItem.value === invoiceGroupId;
    });
  };

  const hasSingleResult = invoiceGroupSelectItems.length === 1;

  let defaultValue: InvoiceGroupSelectItem | undefined;
  if (scheduledBillingInvoiceGroupId) {
    defaultValue = getDefaultBillingGroup(scheduledBillingInvoiceGroupId);
  } else if (entitlementBillingInvoiceGroupId) {
    if (purchaseOrder?.number) {
      const initialValue = getDefaultBillingGroup(entitlementBillingInvoiceGroupId);
      const processedString = initialValue?.label?.split(',');

      const purchaseOrderIndex = findIndex(processedString, (element) => element.includes('Purchase order'));
      const updatedPurchaseOrder = getPurchaseOrderDetails(purchaseOrder);

      if (processedString?.length) {
        processedString[purchaseOrderIndex] = `Purchase order: ${updatedPurchaseOrder}`;
      }

      defaultValue = {
        ...initialValue,
        invoiceGroup: {
          ...initialValue?.invoiceGroup,
          purchaseOrder: {
            ...purchaseOrder,
          },
        },
        label: processedString?.join(','),
      } as InvoiceGroupSelectItem;
    } else {
      defaultValue = getDefaultBillingGroup(entitlementBillingInvoiceGroupId);
    }
  } else if (hasSingleResult && !rewindPaneDefault) {
    defaultValue = invoiceGroupSelectItems[0];
  }

  const handleChange = (fieldProps: FieldProps<ValueType<InvoiceGroupSelectItem>, HTMLSelectElement>) => (
    selectedItem: ValueType<InvoiceGroupSelectItem>
  ): void => {
    if (setSelectedInvoiceGroupPricingPlanValue) {
      setSelectedInvoiceGroupPricingPlanValue(selectedItem?.invoiceGroup.shipToPriceEligibility);
    }

    if (onChange) {
      onChange(selectedItem);
    }

    fieldProps.onChange(selectedItem);
  };

  const wrapperProps: React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLDivElement> & { 'data-testid'?: string },
    HTMLDivElement
  > = {};

  if (testId) {
    wrapperProps['data-testid'] = testId;
  }

  return (
    <div style={maxWidth === null ? {} : { maxWidth: '500px' }} {...wrapperProps}>
      <Field<ValueType<InvoiceGroupSelectItem>, HTMLSelectElement>
        name={fieldName}
        label={fieldLabel}
        isRequired={isRequired}
        isDisabled={isDisabled || isReadOnly}
        defaultValue={defaultValue}
        validate={validate}
      >
        {/* Form does not update the `error` property until a submit it attempted. Instead, we can use the `meta.error` property to access any validation error that may have occurred */}
        {({ fieldProps, meta }) => (
          <div
            data-testid="invoice-group-select-dropdown"
            className={isReadOnly ? 'invoice-group-select-read-only' : ''}
          >
            <Select<InvoiceGroupSelectItem>
              {...fieldProps}
              isLoading={isLoading}
              options={invoiceGroupSelectItems}
              components={{
                Option: InvoiceGroupSelectOption,
                // @ts-ignore: Use the same component for both the Option and SingleValue component
                SingleValue: InvoiceGroupSelectOption,
                ...(isReadOnly && { DropdownIndicator: null }), // Remove dropdown indicator if read-only
              }}
              onChange={handleChange(fieldProps)}
              isSearchable={isSearchable}
              inputId="IG-input-field"
            />
            {meta.error === InvoiceGroupFormFieldErrors.SHIPPING_ADDRESS && (
              <ErrorMessage>Selected billing profile must have a sold-to address to proceed</ErrorMessage>
            )}
            {meta.error === InvoiceGroupFormFieldErrors.PAYMENT_METHOD && (
              <ErrorMessage>Selected billing profile must have a default payment method to proceed</ErrorMessage>
            )}
          </div>
        )}
      </Field>
    </div>
  );
};
